import React from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CircularProgress } from "@mui/material";
import {
  SubscriptionCard,
  // PaymentCardView,
  WarningBox,
  DiscountCodeComponent,
} from "../SharedComponents";
import {
  StyledDialog,
  DialogHeader,
  HeaderTitle,
  Title,
  CloseButton,
  Separator,
  ScrollableContent,
  CardsContainer,
  ChevronContainer,
  MiddleContentContainer,
  WarningContainer,
  ButtonsContainer,
  CancelText,
} from "../SubscriptionModal.styled";
import Button from "../../Button";



/**
 * Modal component for downgrading subscriptions
 */
const DowngradeModal = ({
  open,
  onClose,
  onConfirm,
  fromPlan = {
    type: "premium",
    name: "Premium",
    price: "15",
    validityDate: "Sept 05, 2024",
  },
  toPlan = {
    type: "essential",
    name: "Essential",
    price: "5",
    validityDate: "Sept 06, 2024",
  },
  // showPaymentCard = true,
  warningItems = [],
  closeOnBackdropClick = false,
  loading = false,
  downgradeButtonText = "Downgrade",
  // handlePaymentMethodChange = ()=>{},
  // Promo code props
  promoCode = "",
  discountInfo = null,
  codeError = "",
  validatingCode = false,
  onApplyPromoCode = () => {},
}) => {
  // Check if downgrading to free plan
  const isDowngradingToFree = parseFloat(toPlan.price) === 0;
  // Check if the plan is annual subscription
  const isAnnualSubscription = toPlan.durationDisplay === "Annually";
  
  // Calculate the correct monthly price if discount applied to annual plan
  const getDisplayPrice = () => {
    if (!discountInfo) return toPlan.price;
    
    // If it's an annual plan, divide by 12 to get the monthly price
    if (isAnnualSubscription) {
      return (parseFloat(discountInfo.discountedPrice) / 12).toFixed(2);
    }
    
    return discountInfo.discountedPrice;
  };
  
  // Same for original price if needed
  const getDisplayOriginalPrice = () => {
    if (!discountInfo) return null;
    
    if (isAnnualSubscription) {
      return (parseFloat(discountInfo.originalPrice) / 12).toFixed(2);
    }
    
    return discountInfo.originalPrice;
  };

  const handleBackdropClick = (event) => {
    // Only close if closeOnBackdropClick is true
    if (closeOnBackdropClick) {
      onClose(event);
    }
  };


  // For onConfirm, we should pass the promo code if it's valid
  const handleConfirm = () => {
    onConfirm({
      promoCode: discountInfo ? discountInfo.code : null
    });
  };

  const renderMiddleContent = () => {
    const components = [];

    // Only show discount code component if NOT downgrading to free
    if (!isDowngradingToFree) {
      components.push(
        <DiscountCodeComponent
          key="discount"
          onApplyCode={onApplyPromoCode}
          initialCode={promoCode}
          placeholder="Add Coupon here"
          showTitle={true}
          error={codeError}
          loading={validatingCode}
          discountInfo={discountInfo}
        />
      );
    }

    // Comment out payment component
    /* if (showPaymentCard) {
      components.push(
        <PaymentCardView 
          key="payment"
          onPaymentMethodChanged={handlePaymentMethodChange} 
        />
      );
    } */

    return components.length > 0 ? components : null;
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={!closeOnBackdropClick}
      onClick={(e) => e.stopPropagation()}
      onBackdropClick={handleBackdropClick}
    >
      <DialogHeader>
        <HeaderTitle>
          <WarningAmberIcon style={{ color: "#F79A00" }} />
          <Title>Confirm Your Downgrade</Title>
        </HeaderTitle>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </DialogHeader>

      <Separator />

      <ScrollableContent>
        <CardsContainer>
          <SubscriptionCard
            planType={fromPlan.type}
            planName={fromPlan.name}
            price={fromPlan.price}
            interval="/month"
            validityLabel="Until"
            validityDate={fromPlan.validityDate}
            isActive={true}
            durationDisplay={fromPlan.durationDisplay}
          />

          <ChevronContainer>
            <ChevronRightIcon style={{ color: "#9B5AF8", fontSize: 40 }} />
          </ChevronContainer>

          <SubscriptionCard
            planType={toPlan.type}
            planName={toPlan.name}
            price={getDisplayPrice()}
            originalPrice={getDisplayOriginalPrice()}
            interval="/month"
            validityLabel="Starting from"
            validityDate={toPlan.validityDate}
            durationDisplay={toPlan.durationDisplay}
            yearlyPrice={discountInfo ? discountInfo.discountedPrice : toPlan.yearlyPrice}
          />
        </CardsContainer>

        {renderMiddleContent() && (
          <MiddleContentContainer>
            {renderMiddleContent()}
          </MiddleContentContainer>
        )}

        <WarningContainer>
          <WarningBox isUpgrade={false} warningItems={warningItems} />
        </WarningContainer>
      </ScrollableContent>

      <ButtonsContainer>
        <CancelText onClick={onClose}>Cancel</CancelText>
        <Button width={"auto"} onClick={handleConfirm} disabled={loading}>
          {loading ? (
            <CircularProgress size={20} style={{ color: "white" }} />
          ) : (
            downgradeButtonText
          )}
        </Button>
      </ButtonsContainer>
    </StyledDialog>
  );
};

DowngradeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  fromPlan: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired,
    isAnnual: PropTypes.bool,
    monthlyPrice: PropTypes.string,
    yearlyPrice: PropTypes.string,
  }),
  toPlan: PropTypes.shape({
    type: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    validityDate: PropTypes.string.isRequired,
    price_id: PropTypes.string.isRequired,
    product_id: PropTypes.string.isRequired,
    isAnnual: PropTypes.bool,
    monthlyPrice: PropTypes.string,
    yearlyPrice: PropTypes.string,
  }),
  // showPaymentCard: PropTypes.bool,
  warningItems: PropTypes.arrayOf(PropTypes.string),
  closeOnBackdropClick: PropTypes.bool,
  loading: PropTypes.bool,
  downgradeButtonText: PropTypes.string,
  // handlePaymentMethodChange: PropTypes.func,
  // Promo code props
  promoCode: PropTypes.string,
  discountInfo: PropTypes.object,
  codeError: PropTypes.string,
  validatingCode: PropTypes.bool,
  onApplyPromoCode: PropTypes.func,
};

export default DowngradeModal;
